import type { TErrorId } from "utils/errors/mapApiErrorParams";

import constate from "constate";
import { useCallback, useEffect, useState } from "react";
import { devLog } from "utils/devtools/devLogging";
import { EntitleError } from "utils/errors/entitleError";
import { logrocketLogError } from "utils/logrocket";

const UNAUTHORIZED_ERRORS: TErrorId[] = ["fetch.sessionExpired", "request.unauthenticated", "request.unauthorized"];

const suppressError = (error: Error, isLoginPage: boolean) =>
  (error instanceof EntitleError && error.suppressError)
  || ("errorId" in error && UNAUTHORIZED_ERRORS.includes(error.errorId as TErrorId) && isLoginPage);

const useGlobalError = () => {
  const [error, setError] = useState<Error | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [retryAction, setRetryAction] = useState<(() => void) | undefined>(undefined);
  const [isLoginPage, setIsLoginPage] = useState<boolean>();
  const [pendingError, setPendingError] = useState<null | { error: Error; retryAction?: () => Promise<void> | void }>(
    null,
  );

  const openErrorModal = useCallback(
    (error: Error, retryAction?: () => Promise<void> | void) => {
      // If page is not initialized yet, queue the error
      if (isLoginPage === undefined) {
        setPendingError(() => ({ error, retryAction }));
        return;
      }

      if (suppressError(error, isLoginPage)) return;
      setError(error);
      setRetryAction(() => retryAction);
      setModalOpen(true);
    },
    [isLoginPage],
  );

  // Process pending errors when isLoginPage is initialized
  useEffect(() => {
    if (isLoginPage !== undefined && pendingError) {
      if (!suppressError(pendingError.error, isLoginPage)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Legacy
        openErrorModal(pendingError.error, pendingError.retryAction);
      }
      setPendingError(() => null);
    }
  }, [isLoginPage, openErrorModal, pendingError]);

  const closeErrorModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  useEffect(() => {
    if (!error) return;
    logrocketLogError(error, { location: "GlobalError" });
    devLog({ extra: error, level: "error", message: error.message });
  }, [error]);

  return {
    actions: { closeErrorModal, openErrorModal, retryAction, setIsLoginPage },
    state: { error, isLoginPage, modalOpen },
  };
};

export const [GlobalErrorProvider, useGlobalErrorContext] = constate(useGlobalError);
